import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Shield, AlertCircle, Eye, EyeOff, Mail, RefreshCw, CheckCircle } from 'lucide-react';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const { adminLogin } = useAuth();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // 2FA State
  const [requires2FA, setRequires2FA] = useState(false);
  const [emailHint, setEmailHint] = useState('');
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  
  const otpInputRefs = useRef([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Login failed');
      }
      
      if (data.requires_2fa) {
        // 2FA required - show OTP input
        setRequires2FA(true);
        setEmailHint(data.email_hint || '***@***.com');
        setResendCooldown(30); // 30 second cooldown
        // Focus first OTP input
        setTimeout(() => otpInputRefs.current[0]?.focus(), 100);
      } else {
        // No 2FA - direct login
        await adminLogin(email, password);
        navigate('/admin');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d+$/.test(value)) return;
    
    const newOtp = [...otpCode];
    newOtp[index] = value;
    setOtpCode(newOtp);
    
    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newOtp.every(d => d)) {
      handleVerify2FA(newOtp.join(''));
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOTPPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setOtpCode(newOtp);
      if (pastedData.length === 6) {
        handleVerify2FA(pastedData);
      }
    }
  };

  const handleVerify2FA = async (code) => {
    setError('');
    setVerifying(true);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/verify-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp_code: code })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Verification failed');
      }
      
      // Store token and navigate
      localStorage.setItem('token', data.token);
      localStorage.setItem('isAdmin', 'true');
      localStorage.setItem('adminEmail', email);
      localStorage.setItem('user', JSON.stringify({ email, user_type: 'admin' }));
      
      console.log('Admin 2FA verified, redirecting to /admin');
      
      // Force a full page reload to /admin to ensure auth context reinitializes
      window.location.href = '/admin';
      
    } catch (err) {
      setError(err.message);
      setOtpCode(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setVerifying(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendCooldown > 0) return;
    
    setResending(true);
    setError('');
    
    try {
      const res = await fetch(`${API_URL}/api/admin/resend-2fa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      
      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.detail || 'Failed to resend OTP');
      }
      
      setResendCooldown(30);
      setEmailHint(data.email_hint || emailHint);
    } catch (err) {
      setError(err.message);
    } finally {
      setResending(false);
    }
  };

  const handleBack = () => {
    setRequires2FA(false);
    setOtpCode(['', '', '', '', '', '']);
    setError('');
  };

  // 2FA Verification Screen
  if (requires2FA) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
        <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md">
          <div className="flex items-center justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-rose-500 to-orange-500 rounded-full flex items-center justify-center">
              <Mail className="w-8 h-8 text-white" />
            </div>
          </div>
          
          <h1 className="text-2xl font-bold text-white text-center mb-2">Two-Factor Authentication</h1>
          <p className="text-gray-400 text-center mb-2">Enter the 6-digit code sent to</p>
          <p className="text-rose-400 text-center font-mono mb-8">{emailHint}</p>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-start">
              <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
              <p className="text-red-400">{error}</p>
            </div>
          )}

          {/* OTP Input */}
          <div className="flex justify-center space-x-3 mb-8">
            {otpCode.map((digit, index) => (
              <input
                key={index}
                ref={el => otpInputRefs.current[index] = el}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={(e) => handleOTPChange(index, e.target.value)}
                onKeyDown={(e) => handleOTPKeyDown(index, e)}
                onPaste={handleOTPPaste}
                disabled={verifying}
                className="w-12 h-14 text-center text-2xl font-bold bg-gray-700 border-2 border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-rose-500 focus:border-rose-500 disabled:opacity-50 transition-all"
                data-testid={`otp-input-${index}`}
              />
            ))}
          </div>

          {/* Verify Button */}
          <button
            onClick={() => handleVerify2FA(otpCode.join(''))}
            disabled={verifying || otpCode.some(d => !d)}
            className="w-full py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center mb-4"
            data-testid="verify-2fa-btn"
          >
            {verifying ? (
              <>
                <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                Verifying...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                Verify Code
              </>
            )}
          </button>

          {/* Resend & Back */}
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="text-gray-400 hover:text-white text-sm"
            >
              ← Back to login
            </button>
            
            <button
              onClick={handleResendOTP}
              disabled={resending || resendCooldown > 0}
              className="text-rose-400 hover:text-rose-300 text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {resending ? (
                <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
              ) : null}
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend Code'}
            </button>
          </div>

          <div className="mt-8 p-4 bg-gray-700/50 rounded-xl">
            <p className="text-gray-400 text-sm text-center">
              Check your email inbox for the verification code. It may take a few seconds to arrive.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Normal Login Screen
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="bg-gray-800 rounded-2xl p-8 w-full max-w-md">
        <div className="flex items-center justify-center mb-6">
          <Shield className="w-12 h-12 text-rose-500" />
        </div>
        
        <h1 className="text-2xl font-bold text-white text-center mb-2">Admin Login</h1>
        <p className="text-gray-400 text-center mb-8">VirtualFit Administration</p>

        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 mb-6 flex items-start">
            <AlertCircle className="w-5 h-5 text-red-400 mr-3 flex-shrink-0 mt-0.5" />
            <p className="text-red-400">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-rose-500 focus:border-transparent"
              placeholder="admin@virtualfit.work"
              data-testid="admin-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">Password</label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white focus:ring-2 focus:ring-rose-500 focus:border-transparent pr-12"
                placeholder="••••••••"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-4 bg-gradient-to-r from-rose-500 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50"
            data-testid="admin-login-btn"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link to="/" className="text-gray-400 hover:text-white text-sm">
            ← Back to VirtualFit
          </Link>
        </div>

        {/* 2FA Info */}
        <div className="mt-6 p-4 bg-gray-700/30 rounded-xl border border-gray-600">
          <div className="flex items-center text-gray-400 text-sm">
            <Mail className="w-4 h-4 mr-2 text-rose-400" />
            Protected by email two-factor authentication
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLoginPage;
